<template>
  <div class="pre-analysis-page">
    <pre-analysis-header
      :company="company"
      :analysis="analysis"
      @back="handleBack"
    />

    <pre-analysis-reason
      :reasons="analysis.reasons"
    />

    <pre-analysis-history
      :analysis-id="$route.params.id"
      :history="analysis.history"
      :exception-request="analysis.exceptionRequest"
      :is-reprocessing="isReprocessingPending"
      @request-exception="handleRequestException"
      @replace-file="handleReplaceFile"
      @reprocess-pending="handleReprocessPending"
    />

    <exception-approval-modal
      v-model="showExceptionModal"
      :loading="exceptionLoading"
      :replace-mode="isReplacingFile"
      @approve="handleExceptionApproval"
    />
  </div>
</template>

<script>
import PreAnalysisHeader from './components/PreAnalysis/PreAnalysisHeader.vue'
import PreAnalysisReason from './components/PreAnalysis/PreAnalysisReason.vue'
import PreAnalysisHistory from './components/PreAnalysis/PreAnalysisHistory.vue'
import ExceptionApprovalModal from './components/PreAnalysis/ExceptionApprovalModal.vue'
import CreditAnalysisService from '@/service/credit-analysis'
import { callLoading } from '@/service/utilities'

export default {
  name: 'PreAnalysis',

  components: {
    PreAnalysisHeader,
    PreAnalysisReason,
    PreAnalysisHistory,
    ExceptionApprovalModal
  },

  data() {
    return {
      showExceptionModal: false,
      isReplacingFile: false,
      exceptionRequest: null,
      exceptionLoading: false,
      isReprocessingPending: false,
      company: {
        name: '',
        cnpj: ''
      },
      analysis: {
        status: '',
        analysisDate: '',
        proposal: '',
        reasons: [],
        history: []
      }
    }
  },

  async mounted() {
    await this.loadAnalysis(this.$route.params.id)
  },

  methods: {
    async loadAnalysis(id) {
    callLoading(true)

    try {
      const response = await CreditAnalysisService.getById(id)
      const data = response && response.data
      const result = data && (data.data || data)
      const record = Array.isArray(result) ? result[0] : result

      if (record) {
        this.company = record.company || {
          name: record.company_name,
          document: record.document
        }
        const analysis = record.analysis || record
        const status = analysis.status || (
          typeof analysis.approved !== 'undefined'
            ? (analysis.approved ? 'Aprovada' : 'Reprovada')
            : ''
        )

        this.analysis = {
          ...analysis,
          status,
          reasons: analysis.reasons || [],
          history: Array.isArray(analysis.history)
            ? analysis.history
            : [],
          exceptionRequest: analysis.exceptionRequest || null
        }
      } else {
        this.$toast.error('Análise de crédito não encontrada.')
      }
    } catch (error) {
      this.$toast.error('Não foi possível carregar a análise de crédito.')
    } finally {
      callLoading(false)
      }
    },

    handleBack() {
      this.$router.go(-1)
    },

    async handleReprocessPending() {
      const analysisId = this.$route.params.id

      if (!analysisId) {
        this.$toast.error('Análise não encontrada.')
        return
      }

      this.isReprocessingPending = true

      try {
        const response = await CreditAnalysisService.reprocessPendingSteps(analysisId)

        if (!response || response.status < 200 || response.status >= 300) {
          throw new Error('Não foi possível reprocessar as etapas pendentes.')
        }

        if (response.status === 206) {
          this.$toast.warning(
            'Algumas etapas continuam pendentes devido à indisponibilidade de um ou mais sistemas.'
          )
        } else {
          this.$toast.success('Etapas pendentes reprocessadas com sucesso.')
        }

        await this.loadAnalysis(analysisId)
      } catch (error) {
        this.$toast.error('Não foi possível reprocessar as etapas pendentes.')
      } finally {
        this.isReprocessingPending = false
      }
    },

    handleRequestException() {
      this.isReplacingFile = false
      this.showExceptionModal = true
    },

    async handleReplaceFile() {
      const file = this.analysis.exceptionRequest
        && this.analysis.exceptionRequest.file

      if (!file || !this.canReplaceFile(file.uploadedAt)) {
        this.$toast.error(
          'O prazo de 24 horas para alterar o arquivo foi encerrado.'
        )
        return
      }

      const result = await this.$swal({
        title: 'Alterar arquivo?',
        text: 'O arquivo anterior será substituído após o novo upload.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
        focusConfirm: false,
        focusCancel: false,
        customClass: {
          actions: 'swal-actions'
        },
        didOpen: () => {
          document.activeElement?.blur()
        }
      })

      if (result.isConfirmed) {
        this.isReplacingFile = true
        this.showExceptionModal = true
      }
    },

    canReplaceFile(uploadedAt) {
      if (!uploadedAt) {
        return false
      }

      return Date.now() - new Date(uploadedAt).getTime() < 24 * 60 * 60 * 1000
    },

    async handleExceptionApproval(data) {
      const analysisId = this.$route.params.id
      const responsible = this.$store.state.user.data
        && this.$store.state.user.data.name

      if (!data || !data.file || !analysisId || !responsible) {
        this.$toast.error('Não foi possível preparar a solicitação.')
        return
      }

      const payload = new FormData()

      payload.append('file', data.file)
      payload.append('justification', data.description)
      payload.append('id', analysisId)
      payload.append('responsible', responsible)

      this.exceptionLoading = true

      try {
        const response = this.isReplacingFile
          ? await this.replaceExceptionFile(payload, analysisId)
          : await CreditAnalysisService.validate(payload)

        if (response && response.status >= 200 && response.status < 300) {
          this.$toast.success('Solicitação enviada com sucesso.')

          await this.loadAnalysis(analysisId)

          this.showExceptionModal = false
          this.isReplacingFile = false
        } else {
          this.$toast.error(
            this.getExceptionApprovalErrorMessage(response && response.status)
          )
        }
      } catch (error) {
        const status = error && error.response && error.response.status

        this.$toast.error(this.getExceptionApprovalErrorMessage(status))
      } finally {
        this.exceptionLoading = false
      }
    },

    getExceptionApprovalErrorMessage(status) {
      const messages = {
        404: 'Análise não encontrada.',
        422: 'Já existe um arquivo com esse nome.'
      }

      return messages[status] || 'Não foi possível enviar a solicitação.'
    },

    async replaceExceptionFile(payload, analysisId) {
      const deleteResponse = await CreditAnalysisService.deleteFile(analysisId)

      if (
        !(
          deleteResponse &&
          deleteResponse.status >= 200 &&
          deleteResponse.status < 300
        )
      ) {
        throw new Error('Não foi possível excluir o arquivo anterior.')
      }

      const response = await CreditAnalysisService.importFile(payload)

      return response
    }
  }
}
</script>

<style>
.pre-analysis-page {
  padding: 14px 38px 30px;
  min-height: calc(100vh - 60px);
}

.swal-actions {
  gap: 12px !important;
}

.swal-actions .swal2-confirm {
  margin-left: 0 !important;
}

.swal-actions .swal2-cancel {
  margin-right: 0 !important;
}

</style>